import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { usePtvRecommendations } from '@/api/hooks';
import ServiceCard from '@/components/Card/ServiceCard';
import Link from '@/components/Link';
import SrOnly from '@/components/SrOnly';
import Toast from '@/components/Toast';
import AuroraAiLogo from '@/images/Aurora-logo.png';
import Navigation from '@/routes/Home/Navigation';
import ServicesSkeleton from '@/routes/Services/ServicesSkeleton';
import { useParseRecommendationsPerAnswers, useQueryParams } from '@/utils';

const Background = styled.div`
  background: no-repeat center center url('/app-background.jpg');
  background-attachment: fixed;
  background-size: cover;
  padding: 0 20px;
`;

const HomeHeader = styled.header`
  width: 100%;
  text-align: center;
`;

const Logo = styled.img`
  width: 80px;
  margin: 1.5em 0;
`;

const Heading = styled.h2`
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 31px;
  text-align: center;
  color: ${({ theme }) => theme.text};
`;

const List = styled.ul`
  list-style-type: none;
  margin: 30px 0 0 0;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 60px;
`;

const EmptyListMessage = styled.p`
  text-align: center;
`;

const PtvRecommendations = () => {
  const { t } = useTranslation();

  const params = useQueryParams();
  const answers = JSON.parse(params.answers);
  const { data, error, loading } = usePtvRecommendations(answers[0].kunta);

  const filteredRecommendations = useParseRecommendationsPerAnswers(
    data,
    answers
  );
  const flattenedFilteredRecommendations = filteredRecommendations
    ?.map((recommendation) =>
      Object.keys(answers[1])[0] === 'nuori' && answers[1] === '1'
        ? recommendation?.youthServices
        : recommendation?.services
    )
    .flat(1);
  const duplicatesRemovedRecommendations =
    flattenedFilteredRecommendations?.filter(
      (v, i, a) => a.findIndex((v2) => v2?.id === v?.id) === i
    );

  const ptvRecommendationBackgrounds = [
    'https://images.unsplash.com/photo-1543749247-18150e7fa4b0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1570460147789-4e2e622bbb25?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1580675788857-2f6454ee2b4f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1542825287-aed5cdac94ec?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1532009877282-3340270e0529?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1561567732-5798d353905f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1508838841931-4e23fd38a1cf?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1505455184862-554165e5f6ba?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
    'https://images.unsplash.com/photo-1539541417736-3d44c90da315?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80',
  ];

  const pickRandomBackground = () => {
    return ptvRecommendationBackgrounds[
      Math.floor(Math.random() * ptvRecommendationBackgrounds.length)
    ];
  };

  return (
    <Background>
      <HomeHeader>
        <Link to={'/'}>
          <Logo src={AuroraAiLogo} alt="" />
        </Link>
        <SrOnly as="h1">{t('home.heading')}</SrOnly>
      </HomeHeader>
      <Navigation />

      <Heading>{t('ptvRecommendations.title')}</Heading>

      {error && <Toast message={t('error.backend')} />}

      {loading ? (
        <ServicesSkeleton />
      ) : duplicatesRemovedRecommendations?.length ? (
        <List>
          {duplicatesRemovedRecommendations?.map(
            (service, index) =>
              service && (
                <ListItem key={index}>
                  <ServiceCard
                    service={{
                      ...service,
                      coverPhoto: pickRandomBackground(),
                    }}
                  />
                </ListItem>
              )
          )}
        </List>
      ) : (
        <EmptyListMessage>
          {t('recommendations.noRecommendations')}
        </EmptyListMessage>
      )}
    </Background>
  );
};

export default PtvRecommendations;
